'use client';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useMemo, useRef, useEffect } from 'react';
import type {
  ChartId,
  DrawingSharedState,
  IndicatorsDataStructure,
  RenderNode,
  RenderNodesMap,
} from '../types/commontypes';
import { ShapeType } from '../types/commontypes';
import { IndicatorType } from '../types/commontypes';
import { restoreFromStorage, sanitizeForStorage } from '../hooks/useLocalStorageShapes';
import type { StorageShape, StorageShapeInput, StorageShapesInputMap, StorageShapesMap } from '@/types/shape';
import { nodeToStorageShape } from '../utils/shapeConverter';
import { entries, getShortSymbolName } from '../utils/utils';
import { createShapeStore } from '../stores/shapeStore';
import type { ShapeStore } from '../stores/shapeStore';
import { useStore } from 'zustand';

interface ChartShapesContextValue {
  symbol: string;
  timeframe: string;
  renderNodes: RenderNodesMap;
  setRenderNodes: (newDataOrUpdater: RenderNodesMap | ((prev: RenderNodesMap) => RenderNodesMap)) => void;
  drawState: DrawingSharedState;
  setDrawState: (updater: DrawingSharedState | ((prev: DrawingSharedState) => DrawingSharedState)) => void;
  indicatorsData?: IndicatorsDataStructure;
  setIndicatorsData?: React.Dispatch<React.SetStateAction<IndicatorsDataStructure>>;
}

export interface ChartShapesContextType extends ChartShapesContextValue {
  /** zustand vanilla store 직접 접근 (class 컴포넌트, selector 구독용) */
  shapeStore: ReturnType<typeof createShapeStore>;
}

export const ChartShapesContext = createContext<ChartShapesContextType | undefined>(undefined);

export const defaultIndicatorsData: IndicatorsDataStructure = {
  chartId_volume: [{ id: 'volume', type: IndicatorType.VOLUME, label: 'Volume', options: {} }], // volume chart, no indicators
  chartId_ohlcv: [
    { id: 'ema12', type: IndicatorType.EMA_12, label: 'BTC - 15m', options: { windowSize: 12 } },
    { id: 'ema26', type: IndicatorType.EMA_26, label: 'EMA 26', options: { windowSize: 26 } },
  ],
  chartId_full_sto: [
    {
      id: 'fullSTO',
      type: IndicatorType.FULL_STO,
      label: 'Full STO K%',
      options: { windowSize: 14, kWindowSize: 3, dWindowSize: 4 },
    },
  ],
  chartId_rsi: [{ id: 'rsi', type: IndicatorType.RSI, label: 'RSI', options: { windowSize: 14 } }],
  chartId_elder_ray: [{ id: 'elderRay', type: IndicatorType.ELDER_RAY, label: 'Elder Ray' }],
  chartId_volume_2: [{ id: 'volumeBar1', type: IndicatorType.VOLUME1, label: 'Volume Bar1' }], // volume chart, no indicators
};

interface ChartShapesProviderProps {
  children: ReactNode;
  symbol: string;
  timeframe: string;
  onLoadShapes?: (symbol: string, timeframe: string) => Promise<StorageShapesMap>;
  // onSaveShapes?: (symbol: string, timeframe: string, data: StorageShapesMap) => Promise<void>;
  onUpsertShapes?: (symbol: string, timeframe: string, shapes: StorageShapeInput[]) => Promise<StorageShape[]>;
  onDeleteShapes?: (symbol: string, timeframe: string, uuids: string[]) => Promise<void>;
}

export const ChartShapesProvider: React.FC<ChartShapesProviderProps> = ({
  children,
  symbol,
  timeframe,
  onLoadShapes,
  // onSaveShapes,
  onUpsertShapes,
  onDeleteShapes,
}) => {
  // Generate localStorage key: shapes_BTCUSDT_1h
  const storageKey = useMemo(() => {
    return `shapes_${getShortSymbolName(symbol)}_${timeframe}`;
  }, [symbol, timeframe]);

  // ─── zustand store (symbol+timeframe 당 1개) ───
  // onLoadShapes가 있으면 backend에서 로드하므로 localStorage 초기 로드 생략
  const storeRef = useRef<ReturnType<typeof createShapeStore> | null>(null);
  if (storeRef.current === null) {
    const initialData = onLoadShapes ? {} : loadFromLocalStorage(storageKey);
    storeRef.current = createShapeStore(initialData);
  }
  const store = storeRef.current;

  // symbol/timeframe 변경 시 store를 새 데이터로 리셋
  // onLoadShapes가 있으면 backend에서 로드하므로 localStorage는 사용하지 않음
  const prevKeyRef = useRef(storageKey);
  useEffect(() => {
    if (prevKeyRef.current !== storageKey) {
      const newData = onLoadShapes ? {} : loadFromLocalStorage(storageKey);
      store.getState().setRenderNodes(newData);
      prevKeyRef.current = storageKey;
    }
  }, [storageKey, store, onLoadShapes]);

  // store에서 상태 구독
  const renderNodes = useStore(store, s => s.renderNodes);
  const setRenderNodes = useStore(store, s => s.setRenderNodes);
  const drawState = useStore(store, s => s.drawState);
  const setDrawState = useStore(store, s => s.setDrawState);
  // Indicator objects (EMA 12, EMA 26, RSI, etc.)
  const [indicatorsData, setIndicatorsData] = useState<IndicatorsDataStructure>(defaultIndicatorsData);

  // last saved shapes JSON for deep comparison
  const lastSavedJsonRef = React.useRef<string>('');

  // Load shapes from backend on mount
  React.useEffect(() => {
    if (!onLoadShapes) return;

    const loadShapes = async () => {
      try {
        const data = await onLoadShapes(symbol, timeframe);
        if (data && Object.keys(data).length > 0) {
          // prevent triggering save on load
          lastSavedJsonRef.current = JSON.stringify(data);
          // Backend data is in Shape format — convert to RenderNode for rendering
          const restored = restoreFromStorage(data, [], '');
          store.getState().setRenderNodes(restored);
        }
      } catch (error) {
        console.error('Failed to load shapes:', error);
      }
    };

    loadShapes();
  }, [symbol, timeframe, onLoadShapes, store]);

  // ─── dirty set 기반 delta 저장 또는 전체 저장 (500ms 디바운스) ───
  const dirtyUuids = useStore(store, s => s.dirtyUuids);
  const deletedUuids = useStore(store, s => s.deletedUuids);

  React.useEffect(() => {
    const hasDeltaCallbacks = onUpsertShapes != null || onDeleteShapes != null;
    // const hasFullSave = onSaveShapes != null;

    // 저장 콜백이 하나도 없으면 skip
    if (!hasDeltaCallbacks /*  && !hasFullSave */) return undefined;

    const hasDirty = dirtyUuids.size > 0;
    const hasDeleted = deletedUuids.size > 0;

    // delta 콜백이 있고, dirty/deleted가 있으면 → delta 저장
    if (hasDeltaCallbacks && (hasDirty || hasDeleted)) {
      const timeoutId = setTimeout(() => {
        void (async () => {
          try {
            // ── dirty/deleted 교차 케이스 처리 ──
            // snapshot을 취하여 교차 케이스를 정리한다
            const dirtySnapshot = new Set(dirtyUuids);
            const deletedSnapshot = new Set(deletedUuids);

            // 도형이 dirtyUuids와 deletedUuids 양쪽에 있으면 → dirty에서 제거, delete만 전송
            for (const uuid of deletedSnapshot) {
              dirtySnapshot.delete(uuid);
            }

            // ── dirty 도형 upsert ──
            if (dirtySnapshot.size > 0 && onUpsertShapes) {
              const dirtyShapes = extractDirtyShapes(store.getState().renderNodes, dirtySnapshot);
              if (dirtyShapes.length > 0) {
                const savedShapes = await onUpsertShapes(symbol, timeframe, dirtyShapes);
                // 서버 발급 id를 renderNodes에 반영
                applyServerIdPatches(store, dirtyShapes, savedShapes);
              }
            }

            // ── deleted 도형 delete ──
            if (deletedSnapshot.size > 0 && onDeleteShapes) {
              await onDeleteShapes(symbol, timeframe, [...deletedSnapshot]);
            }

            // ── 성공 시 set 비움 ──
            store.getState().clearDirtySets();
          } catch (error) {
            console.error('Failed to save shapes (delta):', error);
          }
        })();
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    // delta 콜백이 있으면 전체 저장을 사용하지 않음 — dirty/deleted가 비어있으면 저장할 것이 없음
    if (hasDeltaCallbacks) return undefined;

    // delta 콜백이 없을 때만 → 기존 전체 저장 (안전망)
    // if (hasFullSave) {
    //   const sanitizedData = sanitizeForStorage(renderNodes);
    //   const currentJson = JSON.stringify(sanitizedData);

    //   if (lastSavedJsonRef.current === currentJson) {
    //     return undefined;
    //   }

    //   const timeoutId = setTimeout(() => {
    //     onSaveShapes(symbol, timeframe, sanitizedData)
    //       .then(() => {
    //         lastSavedJsonRef.current = currentJson;
    //       })
    //       .catch(error => {
    //         console.error('Failed to save shapes:', error);
    //       });
    //   }, 500);

    //   return () => clearTimeout(timeoutId);
    // }

    return undefined;
  }, [renderNodes, dirtyUuids, deletedUuids, symbol, timeframe, onUpsertShapes, onDeleteShapes, store]);

  // ─── localStorage 저장 (500ms 디바운스) ───
  // onLoadShapes가 있으면 backend가 source of truth이므로 localStorage에 저장하지 않는다.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (onLoadShapes) return undefined;

    // Skip saving on first render (already loaded from localStorage)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    const saveTimer = setTimeout(() => {
      try {
        const sanitizedData = sanitizeForStorage(renderNodes);
        localStorage.setItem(storageKey, JSON.stringify(sanitizedData));
      } catch (error) {
        console.warn(`Error saving renderNodes to localStorage key "${storageKey}":`, error);
      }
    }, 500);

    return () => clearTimeout(saveTimer);
  }, [renderNodes, storageKey, onLoadShapes]);

  const value: ChartShapesContextValue = useMemo(
    () => ({
      symbol,
      timeframe,
      renderNodes,
      setRenderNodes,
      drawState,
      setDrawState,
      indicatorsData,
      setIndicatorsData,
    }),
    [symbol, timeframe, renderNodes, setRenderNodes, drawState, setDrawState, indicatorsData, setIndicatorsData],
  );

  const contextValue: ChartShapesContextType = useMemo(
    () => ({
      ...value,
      shapeStore: store,
    }),
    [value, store],
  );

  return <ChartShapesContext.Provider value={contextValue}>{children}</ChartShapesContext.Provider>;
};

export const useChartShapes = () => {
  const context = useContext(ChartShapesContext);
  if (!context) {
    throw new Error('useChartShapes must be used within ChartShapesProvider');
  }
  return context;
};

/**
 * selector 기반 구독 — shapeStore의 필요한 필드만 구독하여 불필요한 리렌더링 방지.
 *
 * @example
 * const drawState = useShapeStoreSelector(s => s.drawState);
 */
export function useShapeStoreSelector<T>(selector: (state: ShapeStore) => T): T {
  const context = useContext(ChartShapesContext);
  if (!context) {
    throw new Error('useShapeStoreSelector must be used within ChartShapesProvider');
  }
  return useStore(context.shapeStore, selector);
}

// ─── Helper ────────────────────────────────────────────────────────

/**
 * renderNodes에서 dirtyUuids에 해당하는 도형을 추출하여 StorageShape 배열로 변환한다.
 * 좌표는 nodeToStorageShape로 재조립만 수행한다 (index→timestamp 변환 없음).
 */
function extractDirtyShapes(renderNodes: RenderNodesMap, dirtyUuids: Set<string>): StorageShapeInput[] {
  const result: StorageShapeInput[] = [];

  for (const chartShapes of Object.values(renderNodes)) {
    for (const [type, shapes] of Object.entries(chartShapes)) {
      if (type === ShapeType.MEASURE) continue;
      if (!shapes || !Array.isArray(shapes)) continue;
      for (const node of shapes as RenderNode[]) {
        if (!dirtyUuids.has(node.uuid)) continue;
        try {
          result.push(nodeToStorageShape(node));
        } catch (e) {
          console.error(`[extractDirtyShapes] Failed to convert node:`, node.type, node.uuid, e);
        }
      }
    }
  }

  return result;
}

/**
 * upsert 응답에서 서버가 발급한 id를 renderNodes에 반영한다.
 * dirtyShapes(요청)와 savedShapes(응답)를 순서 기준으로 매칭한다.
 * uuid는 클라이언트가 발급한 값을 서버가 그대로 보존하므로 패치하지 않는다.
 */
function applyServerIdPatches(
  store: ReturnType<typeof createShapeStore>,
  dirtyShapes: StorageShapeInput[],
  savedShapes: StorageShape[],
): void {
  if (dirtyShapes.length !== savedShapes.length) {
    console.warn(`[applyServerIdPatches] length mismatch: sent ${dirtyShapes.length}, received ${savedShapes.length}`);
  }

  // id가 새로 발급된 도형만 수집
  const idPatches = new Map<string, number>();
  for (let i = 0; i < dirtyShapes.length && i < savedShapes.length; i++) {
    const req = dirtyShapes[i];
    const res = savedShapes[i];
    if (res.id !== undefined && req.id !== res.id) {
      idPatches.set(req.uuid, res.id);
    }
  }

  if (idPatches.size === 0) return;

  store.getState().setRenderNodes(prev => {
    const next: RenderNodesMap = {};
    for (const [chartId, chartShapes] of entries(prev)) {
      const nextChart: typeof chartShapes = {};
      for (const [type, shapes] of entries(chartShapes)) {
        if (!shapes || !Array.isArray(shapes)) {
          nextChart[type] = shapes;
          continue;
        }

        (nextChart as Record<string, unknown>)[type] = shapes.map((s: RenderNode) => {
          const newId = idPatches.get(s.uuid);
          if (newId !== undefined) {
            return { ...s, id: newId };
          }
          return s;
        });
      }

      next[chartId] = nextChart;
    }
    return next;
  });
}

/** localStorage에서 ShapesData를 로드하여 RenderNode 형태로 변환한다. */
function loadFromLocalStorage(storageKey: string): RenderNodesMap {
  if (typeof window === 'undefined') return {};
  try {
    const item = localStorage.getItem(storageKey);
    if (item) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const parsed = JSON.parse(item) as StorageShapesInputMap;
      return restoreFromStorage(parsed, [], '');
    }
  } catch (error) {
    console.warn(`Error loading renderNodes from localStorage key "${storageKey}":`, error);
  }
  return {};
}
